/**
 * Auth composable for user authentication and state management
 * Uses global refs instead of storage - always calls /api/users/about-me for user data
 */

import { UserService } from '@services/UserService';
import { computed, readonly, ref, shallowRef } from 'vue';
import router from '@/router';

/**
 * Key for storing tenant id in localStorage
 * TenantId is crucial for many UI operations. When switching tenant, we need to make sure its updated in all
 * other open pages/tabs to prevent any issues with data consistency.
 */
const TENANT_SYNC_KEY = 'current-tenant-id';

/**
 * Flag to ensure cross-tab sync is only initialized once
 */
let crossTabSyncInitialized = false;

/**
 * Singleton instance of auth composable
 * Stored in shallowRef to ensure same instance is returned every time
 */
const authInstance = shallowRef<ReturnType<typeof createAuthInstance> | null>(null);

/**
 * Create auth composable instance
 * This function is only called once to create the singleton
 * All state and logic is encapsulated within this function
 */
function createAuthInstance() {
  const updateTenantIdKey = (value = window.crypto.randomUUID()): boolean => {
    try {
      localStorage.setItem(TENANT_SYNC_KEY, value);
      return true;
    } catch {
      return false;
    }
  };
  const removeTenantIdKey = (): boolean => {
    try {
      localStorage.removeItem(TENANT_SYNC_KEY);
      return true;
    } catch {
      return false;
    }
  };

  const userState = {
    username: ref<string | null>(null),
    tenantId: ref<string | null>(null),
    tenantName: ref<string | null>(null),
    roles: ref<string[]>([]),
    email: ref<string | null>(null),
    userId: ref<string | null>(null),
    userFullName: ref<string | null>(null),
  };

  const unsetUserState = (): void => {
    userState.username.value = null;
    userState.tenantId.value = null;
    userState.tenantName.value = null;
    userState.roles.value = [];
    userState.email.value = null;
    userState.userId.value = null;
    userState.userFullName.value = null;

    removeTenantIdKey();
  };

  const userService = new UserService();
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Fetch current user information from /api/users/about-me
   * Never uses storage - always fetches from API
   */
  const fetchAboutMe = async (): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;

      const res = await userService.getAboutMe();
      if (!res.success) {
        throw res.err;
      }
      const data = res.response;

      userState.username.value = data.username || null;
      userState.tenantId.value = data.tenantId || null;
      userState.tenantName.value = data.tenantName || null;
      userState.roles.value = Array.isArray(data.roles) ? data.roles : [];
      userState.email.value = data.email || null;
      userState.userId.value = data.userId || null;
      userState.userFullName.value = data.userFullName || null;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch user information';
      unsetUserState();
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Initialize cross-tab synchronization listener
   * This runs once when the instance is first created
   */
  const initCrossTabSync = (): void => {
    if (crossTabSyncInitialized || typeof window === 'undefined') {
      return;
    }

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === TENANT_SYNC_KEY && e.newValue !== e.oldValue) {
        // some pages like dashboards can have data which is fetched using context of different tenant; so we need page refresh
        // fetchAboutMe().catch(() => {});
        router.go(0);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    crossTabSyncInitialized = true;
  };

  /**
   * Login with credentials
   * @param credentials - Login credentials
   */
  const login = async (credentials: Record<string, unknown>): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;

      const loginRes = await userService.login(credentials);
      if (!loginRes.success) throw loginRes.err;
      await fetchAboutMe();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Logout current user
   */
  const logout = async (): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;

      const logoutRes = await userService.logout();
      if (!logoutRes.success) throw logoutRes.err;

      unsetUserState();

      router.push({ name: 'Login' });
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Logout failed';
      unsetUserState();
      router.push({ name: 'Login' });
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Switch tenant
   * @param tenantData - Tenant switch data
   */
  const switchTenant = async (tenantData: Record<string, unknown>): Promise<void> => {
    try {
      isLoading.value = true;
      error.value = null;

      const switchRes = await userService.switchTenant(tenantData);
      if (!switchRes.success) throw switchRes.err;
      updateTenantIdKey();
      router.go(0);
      // await fetchAboutMe();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to switch tenant';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = computed(() => !!userState.username.value);

  initCrossTabSync();

  return {
    username: readonly(userState.username),
    tenantId: readonly(userState.tenantId),
    tenantName: readonly(userState.tenantName),
    roles: readonly(userState.roles),
    email: readonly(userState.email),
    userId: readonly(userState.userId),
    userFullName: readonly(userState.userFullName),
    isLoading: readonly(isLoading),
    error: readonly(error),
    isAuthenticated,
    fetchAboutMe,
    login,
    logout,
    switchTenant,
  };
}

/**
 * Auth composable
 * Returns the same singleton instance every time
 * Provides authentication state and methods
 */
export function useAuth() {
  if (!authInstance.value) {
    authInstance.value = createAuthInstance();
  }
  return authInstance.value;
}
